import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Score } from './Score.component';
import ArrowDown from '../../assets/icons/arrow-down-score.svg';
import ArrowUp from '../../assets/icons/arrow-up-score.svg';
import ArrowDownDisabled from '../../assets/icons/arrow-down-score-disabled.svg';
import ArrowUpDisabled from '../../assets/icons/arrow-up-score-disabled.svg';
import { useStyles } from './ScoresList.styles';
import { useScoring } from '../../Context/Scoring.context';
import { AnnotationOnImage } from '../../models/annotation.types';
import { highContrastMode } from '../../utils/highContrastChecker';
import { Prompt } from '../Prompt/Prompt.component';
import useSessionUser from '../../hooks/useSessionUser';
import { Status } from '../../services/exams/assessmentSession/assessments.types';

interface ScoresListProps {
  isOpen: boolean;
  disabled?: boolean;
  gradingAnnotationAddedToCriterion: AnnotationOnImage[] | undefined;
  onCommentChanged: () => void;
}

export const ScoresList: React.FC<ScoresListProps> = (props) => {
  const classes = useStyles();
  const {
    activeScore,
    activeCriterionDependentScores,
    updateScore,
    activeCriterion,
    isCommentForCurrentScoreProvided,
    examTaskGroupSolutionWithTasks,
  } = useScoring();

  const [scoreListTransform, setScoreListTransform] = useState<number>(0);
  const [examScoreValuesHeight, setExamScoreValuesHeight] = useState<number | undefined>(undefined);
  const [containerHeight, setContainerHeight] = useState<number | undefined>();
  const [examScoreValuesOverflow, setExamScoreValuesOverflow] = useState<number | undefined>(0);
  const [updateScoreModalOn, setUpdateScoreModalOn] = useState<boolean>(false);
  const [scoreValue, setScoreValue] = useState<number | null>(null);
  const { isUserCZE, isUserCZWO } = useSessionUser();

  const container = useRef<HTMLDivElement | null>(null);

  const border = highContrastMode ? 'solid 1px #FFFFFF' : 'none';

  const handleOnClick = async (value: number) => {
    const isCommentForNewScoreRequired = activeCriterion?.examScoreValues.find(w => w.score === value)?.isCommentRequired;
    const prevScore = activeCriterion?.score;
    if (
      isCommentForCurrentScoreProvided &&
      !isCommentForNewScoreRequired &&
      prevScore !== null &&
      (isUserCZE || isUserCZWO) &&
      (examTaskGroupSolutionWithTasks?.examTaskGroupSolutionScoreState === Status.New ||
        examTaskGroupSolutionWithTasks?.examTaskGroupSolutionScoreState === Status.Open)
    ) {
      setScoreValue(value);
      setUpdateScoreModalOn(true);
    } else {
      await updateScore(value);
      props.onCommentChanged();
    }
  };

  const updateScoreAndRemoveComment = async () => {
    await updateScore(scoreValue);
    setUpdateScoreModalOn(false);
    props.onCommentChanged();
  };

  const handleListScroll = (direction: 'up' | 'down') => {
    switch (direction) {
      case 'up':
        {
          if (examScoreValuesOverflow && examScoreValuesOverflow > scoreListTransform / 42 - 1) {
            setScoreListTransform(scoreListTransform + 42);
          } else return;
        }
        break;
      case 'down':
        {
          if (scoreListTransform === 0) return;
          setScoreListTransform(scoreListTransform - 42);
        }
        break;
    }
  };

  const getExamScoreValuesHeight = () => {
    setExamScoreValuesHeight(activeCriterionDependentScores.length * 42);
  };

  const getListSize = () => {
    const newHeight = container.current?.clientHeight;
    setContainerHeight(newHeight);
    setScoreListTransform(0);
    if (newHeight) {
      if (activeCriterionDependentScores.length * 42 - newHeight > 0) {
        setExamScoreValuesOverflow(Math.round(activeCriterionDependentScores.length * 42 - newHeight) / 42);
      } else setExamScoreValuesOverflow(0);
    }
  };

  useEffect(() => {
    getExamScoreValuesHeight();
    getListSize();
  }, [activeCriterionDependentScores]);

  useEffect(() => {
    window.addEventListener('resize', getListSize);
    return () => window.removeEventListener('resize', getListSize);
  }, []);

  return (
    <>
      {props.isOpen && activeCriterionDependentScores.length > 0 && (
        <div
          className={[classes.container, props.disabled ? classes.containerDisabled : classes.containerEnabled].join(
            ' ',
          )}
          style={{ border: border }}
          ref={container}
        >
          <div
            id="scoreList"
            style={{ transform: `translateY(${scoreListTransform}px)` }}
            className={classes.scoreList}
          >
            {activeCriterionDependentScores
              .map((e) => e.score)
              .slice(0)
              .reverse()
              .map((scoreValue: number) => (
                <Score
                  key={scoreValue}
                  score={scoreValue}
                  onClick={handleOnClick}
                  isActive={scoreValue === activeScore}
                  disabled={
                    scoreValue === 0
                      ? props.gradingAnnotationAddedToCriterion !== undefined &&
                        props.gradingAnnotationAddedToCriterion.length > 0
                      : props.disabled
                  }
                />
              ))}
          </div>
          {examScoreValuesHeight && containerHeight && examScoreValuesHeight > containerHeight && (
            <div className={classes.arrowsContainer}>
              <img
                onClick={() => {
                  handleListScroll('up');
                }}
                className={classes.arrowIcon}
                src={
                  examScoreValuesOverflow && examScoreValuesOverflow > scoreListTransform / 42 - 1
                    ? ArrowUp
                    : ArrowUpDisabled
                }
                style={{
                  pointerEvents:
                    examScoreValuesOverflow && examScoreValuesOverflow > scoreListTransform / 42 - 1 ? 'auto' : 'none',
                }}
                alt="ikona poprzednie kryterium"
              />
              <img
                onClick={() => {
                  handleListScroll('down');
                }}
                className={classes.arrowIcon}
                style={{
                  pointerEvents: scoreListTransform === 0 ? 'none' : 'auto',
                }}
                src={scoreListTransform === 0 ? ArrowDownDisabled : ArrowDown}
                alt="ikona następne kryterium"
              />
            </div>
          )}
        </div>
      )}

      <Prompt
        type="confirmation"
        size="sm"
        open={updateScoreModalOn}
        onConfirm={updateScoreAndRemoveComment}
        onCancel={() => setUpdateScoreModalOn(false)}
        mainActionButtonText="TAK"
        cancelButtonText="NIE"
        isBold={true}
        mainText={
          'Zmieniłeś punktację dla kryterium. Aktualna ocena nie wymaga komentarza. Czy chcesz usunąć istniejący komentarz i zapisać zmiany?'
        }
      />
    </>
  );
};
